/**
 * VersionWatcher — mounts the version-drift poller and surfaces a
 * persistent toast with a "Refresh" CTA when the deployed build's
 * SHA no longer matches this tab's SHA.
 *
 * Renders nothing. Lives inside `<Toaster>` in `__root.tsx` so the
 * `toastManager` viewport is already wired by the time we add the
 * toast.
 *
 * Why a toast (not auto-reload):
 *   - The user may be mid-task (running an audit, typing into the
 *     name field, scrolling a long report). Yanking the page out
 *     from under them mid-input is worse than serving slightly-old
 *     UI for a moment longer.
 *   - The reactive `stale-chunk` recovery handles the catastrophic
 *     case (chunk 404 → reload). This is the polite layer: tell the
 *     user "an update is ready" so they refresh on their own clock.
 *
 * Toast properties (see `@base-ui/react/toast` types):
 *   - `timeout: 0` — never auto-dismiss. The signal stays put until
 *     the user clicks "Refresh" or the X.
 *   - `priority: 'low'` — politely announced; not urgent.
 *   - `actionProps.children: 'Refresh'` triggers `window.location.reload()`.
 */
import { toastManager } from '@/components/ui/toast';
import { startVersionCheck } from '@/lib/version-check';
import { type ReactElement, useEffect } from 'react';

export function VersionWatcher(): ReactElement | null {
  useEffect(() => {
    const cleanup = startVersionCheck({
      onDrift: ({ serverVersion, clientVersion }) => {
        // Log so the version pair is in console for support — handy
        // when a user says "I refreshed and it's still broken" and
        // we want to confirm which SHA they ended up on.
        console.info(
          '[version-check] drift detected — refresh to update',
          `(client=${clientVersion.slice(0, 7)} server=${serverVersion.slice(0, 7)})`,
        );
        toastManager.add({
          type: 'info',
          title: 'An update is available',
          description: 'Refresh the page to load the latest version.',
          timeout: 0,
          priority: 'low',
          actionProps: {
            children: 'Refresh',
            onClick: () => {
              if (typeof window !== 'undefined') window.location.reload();
            },
          },
        });
      },
    });
    return cleanup;
  }, []);
  return null;
}
